import { useEffect, useState } from 'react';
import {
  Button,
  SvgIcon,
  TextField,
  HeroBanner,
  COLOR_WHEEL_TOOL,
  COLOR_PICKER_TOOL,
  COLOR_PALETTE_TOOL,
  FONT_GENERATOR_TOOL,
  ToolCategory,
  WithLanding,
} from '@quicktools/shared-components';
import { useTranslation } from 'react-i18next';

import analytics from '../../services/analytics';
import { useStore } from '../../store';

import useStyles from './styles';
import { IHome } from './types';
import { isValidHexColor } from './hexColorValidation';
import HowToSection from './HowToSection';
import ImageSection from './ImageSection';
import WhySection from './WhySection';
import FaqSection from './FaqSection';

const PLACEHOLDER_COLOR = '#A9A9AB';

const TOOLS = [COLOR_WHEEL_TOOL, COLOR_PICKER_TOOL, FONT_GENERATOR_TOOL];

function Home({ hexColor, setHexColor, setShowHomepage }: IHome) {
  const [validHexColor, setValidHexColor] = useState<boolean>(true);
  const [eventPosition, setPosition] = useState<number>(1);
  const classes = useStyles({ hasError: !validHexColor });
  const { t } = useTranslation();

  const {
    state: { hasUrlColors },
  } = useStore();

  useEffect(() => {
    if (hasUrlColors) {
      setHexColor(null);
      setShowHomepage(false);
    }
    analytics.landingPageOpen();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (hexColor != null) {
        analytics.textboxType(eventPosition);
      }
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [hexColor]);

  const handleOnPaste = (position: number) => {
    analytics.textboxPaste(position);
  };

  const handleRandomPallete = (position: number) => {
    setHexColor(null);
    analytics.randomPaletteGenerate(position);
    setShowHomepage(false);
  };
  const handleGeneratePallete = (position: number) => {
    const isValid = isValidHexColor(hexColor);
    setValidHexColor(isValid);
    if (isValid) {
      analytics.paletteGenerate(position, 'success');
      setShowHomepage(false);
    } else {
      analytics.paletteGenerate(position, 'error');
    }
  };
  const handleTyping = (value: string, position: number) => {
    setPosition(position);
    if (value == '') {
      setHexColor(null);
    } else {
      setHexColor(value.trim());
    }

    setTimeout(() => {
      setValidHexColor(isValidHexColor(value));
    }, 500);
  };

  const validationMessage = (className: string) => {
    return !validHexColor ? (
      <p className={className}>
        {t('home.validationMessage.0')}
        <a
          href="/color/picker"
          target={'_blank'}
          rel="noreferrer"
          onClick={() => analytics.clickHere()}
        >
          click here
        </a>
        {t('home.validationMessage.1')}
      </p>
    ) : null;
  };

  const homeButtons = (position: number) => {
    return (
      <div className={classes.optionsContainer}>
        <TextField
          onPaste={() => handleOnPaste(position)}
          placeholder={PLACEHOLDER_COLOR}
          className={classes.textField}
          size="small"
          onChange={(value) => handleTyping(value, position)}
          value={hexColor ?? ''}
        ></TextField>
        {validationMessage(classes.smallDeviceErrorMessage)}
        <Button
          className={classes.generateButtonClass}
          onClick={() => {
            handleGeneratePallete(position);
          }}
        >
          {t('home.generateBtn')}
        </Button>
        <div className={classes.randomButtonWrap}>
          {validationMessage(classes.errorMessage)}
          <Button
            variant={'secondary'}
            className={classes.randomButtonClass}
            onClick={() => {
              handleRandomPallete(position);
            }}
          >
            <SvgIcon url={'https://cdn140.picsart.com/02491039191207457142.svg'}></SvgIcon>

            {t('home.randomBtn')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <WithLanding
      analytics={analytics}
      currentTool={COLOR_PALETTE_TOOL}
      similarTools={TOOLS}
      similarToolsParagraph={t('home.similarParagraph')}
      toolCategory={ToolCategory.Design}
    >
      <HeroBanner
        partialBg
        className={classes.heroBanner}
        heading={t('home.heroBanner.heading')}
        content={t('home.heroBanner.content')}
        imgAlt={t('home.heroBanner.imgAlt')}
        imgSrc="https://cdn140.picsart.com/63316468834086029658.webp"
        footer={homeButtons(1)}
      />

      <ImageSection />

      <HowToSection cta={homeButtons(2)} />

      <WhySection />

      <FaqSection />
    </WithLanding>
  );
}

export default Home;
